import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, ImageIcon, Briefcase, Eye, ArrowRight, Loader2 } from "lucide-react";
import { adminGet } from "../../lib/api";

const Card = ({ to, icon: Icon, label, value, color = "#0A192F", testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="block bg-white border border-gray-200 p-6 hover:border-[#D4AF37] transition-colors"
  >
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 flex items-center justify-center" style={{ background: color }}>
        <Icon size={18} className="text-[#D4AF37]" />
      </div>
      <div className="font-display text-[11px] uppercase tracking-[0.3em] text-[#0A192F]/60">
        {label}
      </div>
    </div>
    <div className="mt-6 font-display font-black text-4xl text-[#0A192F] tracking-tighter">
      {value}
    </div>
    <div className="mt-3 inline-flex items-center gap-2 font-display text-[11px] uppercase tracking-wider text-[#0A192F]/60">
      View <ArrowRight size={12} />
    </div>
  </Link>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState({ leads: "-", media: "-", services: "-", gallery: "-" });
  const [loading, setLoading] = useState(true);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [leads, media, services, gallery] = await Promise.all([
          adminGet("/leads"),
          adminGet("/media"),
          adminGet("/services"),
          adminGet("/gallery"),
        ]);
        setStats({
          leads: leads.length,
          media: media.length,
          services: services.length,
          gallery: gallery.length,
        });
        setRecent(leads.slice(0, 5));
      } catch (e) {
        console.warn("admin dashboard: failed to load stats", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <header className="mb-10">
        <div className="font-display text-[11px] uppercase tracking-[0.3em] text-[#0A192F]/60">
          Welcome back
        </div>
        <h1 className="mt-2 font-display font-black text-4xl text-[#0A192F] tracking-tighter">
          Dashboard
        </h1>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#0A192F]/40">
          <Loader2 className="animate-spin mr-2" size={18} /> Loading…
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card to="/admin/leads" icon={Inbox} label="Leads" value={stats.leads} testId="dash-card-leads" />
            <Card to="/admin/media" icon={ImageIcon} label="Media" value={stats.media} testId="dash-card-media" />
            <Card to="/admin/services" icon={Briefcase} label="Services" value={stats.services} testId="dash-card-services" />
            <Card to="/admin/gallery" icon={ImageIcon} label="Gallery" value={stats.gallery} testId="dash-card-gallery" />
          </div>

          <section className="mt-12">
            <div className="flex items-end justify-between mb-5">
              <h2 className="font-display font-bold text-xl text-[#0A192F]">Recent Leads</h2>
              <Link
                to="/admin/leads"
                className="text-[11px] font-display uppercase tracking-wider text-[#0A192F]/60 hover:text-[#D4AF37]"
              >
                View all →
              </Link>
            </div>
            <div className="bg-white border border-gray-200">
              {recent.length === 0 ? (
                <div className="p-10 text-center text-[#0A192F]/50 text-sm">
                  No leads yet. They'll appear here when someone submits the contact form.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[#F0F4F8] text-[10px] font-display uppercase tracking-wider text-[#0A192F]/60">
                    <tr>
                      <th className="text-left px-4 py-3">Date</th>
                      <th className="text-left px-4 py-3">Name</th>
                      <th className="text-left px-4 py-3 hidden sm:table-cell">Email</th>
                      <th className="text-left px-4 py-3 hidden md:table-cell">Project</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((l) => (
                      <tr key={l.id} className="border-t border-gray-100">
                        <td className="px-4 py-3 text-[#0A192F]/60 text-xs">
                          {new Date(l.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 font-semibold text-[#0A192F]">{l.name}</td>
                        <td className="px-4 py-3 text-[#0A192F]/70 hidden sm:table-cell">{l.email}</td>
                        <td className="px-4 py-3 text-[#0A192F]/70 hidden md:table-cell">{l.project_type}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          <section className="mt-12 bg-[#F0F4F8] p-8 border-l-4 border-[#D4AF37]">
            <div className="flex items-start gap-4">
              <Eye size={20} className="text-[#D4AF37] mt-1" />
              <div>
                <h3 className="font-display font-bold text-[#0A192F]">First time using the admin?</h3>
                <p className="mt-2 text-sm text-[#0A192F]/70">
                  Check out the{" "}
                  <Link to="/admin/help" className="text-[#0A192F] underline hover:text-[#D4AF37] font-semibold">
                    How to Edit the Site
                  </Link>{" "}
                  guide - it walks you through every section step by step.
                </p>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
